import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Checkbox,
  TimePicker,
  Card,
  Row,
  Col,
  Button,
  message,
  Space,
  Tag,
  Descriptions
} from 'antd';
import {
  SafetyOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import { UserLicenseConfig as LicenseConfig, UpdateLicenseConfigData } from '../services/admin';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const parseTimeToDayjs = (timeString: string): dayjs.Dayjs | null => {
  if (!timeString || typeof timeString !== 'string') return null;
  const [hourStr, minuteStr] = timeString.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  if (isNaN(hour) || isNaN(minute)) return null;
  return dayjs().hour(hour).minute(minute);
};

interface UserLicenseConfigProps {
  config?: LicenseConfig | null;
  onSave: (data: UpdateLicenseConfigData) => Promise<void>;
  loading?: boolean;
}

const UserLicenseConfig: React.FC<UserLicenseConfigProps> = ({
  config,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isActive = Form.useWatch('isActive', form);

  const weekdays = [
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
    { label: 'Sunday', value: 0 },
  ];

  const regions = [
    'North America', 'South America', 'Europe', 'Asia', 'Africa',
    'Oceania', 'Middle East', 'Central America', 'Caribbean'
  ];

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        maxDevices: config.maxDevices,
        maxConcurrentPlays: config.maxConcurrentPlays,
        licenseDays: config.licenseDays,
        maxPlayCount: config.maxPlayCount,
        allowVideo: config.allowVideo,
        allowAudio: config.allowAudio,
        allowDocument: config.allowDocument,
        allowedContentTypes: config.allowedContentTypes,
        restrictedContentTypes: config.restrictedContentTypes,
        allowPlayStartTime: parseTimeToDayjs(config.allowPlayStartTime),
        allowPlayEndTime: parseTimeToDayjs(config.allowPlayEndTime),
        allowWeekdays: config.allowWeekdays,
        allowedRegions: config.allowedRegions,
        restrictedRegions: config.restrictedRegions,
        allowedIPs: config.allowedIPs,
        restrictedIPs: config.restrictedIPs,
        isActive: config.isActive,
        notes: config.notes,
      });
    } else {
      form.setFieldsValue({
        maxDevices: 3,
        maxConcurrentPlays: 1,
        licenseDays: 7,
        maxPlayCount: 999999999,
        allowVideo: true,
        allowAudio: true,
        allowDocument: true,
        allowedContentTypes: ['all'],
        restrictedContentTypes: [],
        allowPlayStartTime: parseTimeToDayjs('00:00'),
        allowPlayEndTime: parseTimeToDayjs('23:59'),
        allowWeekdays: [0, 1, 2, 3, 4, 5, 6],
        allowedRegions: [],
        restrictedRegions: [],
        allowedIPs: [],
        restrictedIPs: [],
        isActive: true,
        notes: '',
      });
    }
  }, [config, form]);

  const handleSave = async (values: UpdateLicenseConfigData) => {
    setSaving(true);
    try {
      const formattedValues = {
        ...values,
        allowPlayStartTime: values.allowPlayStartTime
          ? dayjs(values.allowPlayStartTime).format('HH:mm')
          : undefined,
        allowPlayEndTime: values.allowPlayEndTime
          ? dayjs(values.allowPlayEndTime).format('HH:mm')
          : undefined,
      };

      if (formattedValues.allowedContentTypes &&
          formattedValues.allowedContentTypes.length === 1 &&
          formattedValues.allowedContentTypes[0] === 'all') {
        formattedValues.allowedContentTypes = [];
      }

      await onSave(formattedValues);
      message.success('License configuration saved successfully');
    } catch (error: any) {
      message.error(error.message || 'Failed to save license configuration');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="user-license-config">
      <Form form={form} layout="vertical" onFinish={handleSave} disabled={loading}>
        <Row gutter={[16, 16]}>
          {/* Status Card */}
          <Col span={24}>
            <Card>
              <Descriptions bordered>
                <Descriptions.Item label="Status" span={3}>
                  <Space>
                    <Form.Item name="isActive" valuePropName="checked" noStyle>
                      <Switch loading={loading} />
                    </Form.Item>
                    <Tag color={isActive ? 'green' : 'red'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Tag>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Usage Restrictions */}
          <Col span={24}>
            <Card title={<><SafetyOutlined /> Usage Restrictions</>}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Max Devices"
                    name="maxDevices"
                    rules={[{ required: true, message: 'Please input max devices' }]}
                  >
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Max Concurrent Plays"
                    name="maxConcurrentPlays"
                    rules={[{ required: true, message: 'Please input max concurrent plays' }]}
                  >
                    <InputNumber min={1} max={20} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="License Duration (days)"
                    name="licenseDays"
                    rules={[{ required: true, message: 'Please input license duration' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Max Play Count"
                    name="maxPlayCount"
                    rules={[{ required: true, message: 'Please input max play count' }]}
                  >
                    <InputNumber min={1} max={999999999} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Content Type Restrictions */}
          <Col xs={24} lg={12}>
            <Card title={<><GlobalOutlined /> Content Type Restrictions</>}>
              <Form.Item label="Video Content" name="allowVideo" valuePropName="checked">
                <Switch checkedChildren={<CheckCircleOutlined />} unCheckedChildren={<StopOutlined />} />
              </Form.Item>
              <Form.Item label="Audio Content" name="allowAudio" valuePropName="checked">
                <Switch checkedChildren={<CheckCircleOutlined />} unCheckedChildren={<StopOutlined />} />
              </Form.Item>
              <Form.Item label="Document Content" name="allowDocument" valuePropName="checked">
                <Switch checkedChildren={<CheckCircleOutlined />} unCheckedChildren={<StopOutlined />} />
              </Form.Item>
              <Form.Item label="Specific Content Types" name="allowedContentTypes">
                <Select mode="tags" placeholder="Enter specific file extensions" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Restricted Content Types" name="restrictedContentTypes">
                <Select mode="tags" placeholder="Enter restricted file extensions" style={{ width: '100%' }} />
              </Form.Item>
            </Card>
          </Col>

          {/* Time Restrictions */}
          <Col xs={24} lg={12}>
            <Card title={<><ClockCircleOutlined /> Time Restrictions</>}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Play Start Time" name="allowPlayStartTime" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Play End Time" name="allowPlayEndTime" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Allowed Weekdays" name="allowWeekdays">
                <Checkbox.Group style={{ width: '100%' }}>
                  <Row>
                    {weekdays.map(day => (
                      <Col span={8} key={day.value}>
                        <Checkbox value={day.value}>{day.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </Card>
          </Col>

          {/* Geographic Restrictions */}
          <Col xs={24} lg={12}>
            <Card title={<><EnvironmentOutlined /> Geographic Restrictions</>}>
              <Form.Item label="Allowed Regions" name="allowedRegions">
                <Select mode="multiple" style={{ width: '100%' }}>
                  {regions.map(region => <Option key={region} value={region}>{region}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="Restricted Regions" name="restrictedRegions">
                <Select mode="multiple" style={{ width: '100%' }}>
                  {regions.map(region => <Option key={region} value={region}>{region}</Option>)}
                </Select>
              </Form.Item>
            </Card>
          </Col>

          {/* IP Restrictions */}
          <Col xs={24} lg={12}>
            <Card title={<><SettingOutlined /> IP Restrictions</>}>
              <Form.Item label="Allowed IPs" name="allowedIPs">
                <Select mode="tags" style={{ width: '100%' }} placeholder="Enter allowed IP addresses" />
              </Form.Item>
              <Form.Item label="Restricted IPs" name="restrictedIPs">
                <Select mode="tags" style={{ width: '100%' }} placeholder="Enter restricted IP addresses" />
              </Form.Item>
            </Card>
          </Col>

          {/* Notes + Save */}
          <Col span={24}>
            <Card>
              <Form.Item label="Notes" name="notes">
                <TextArea rows={3} maxLength={500} showCount placeholder="Add notes" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" onClick={() => form.submit()} loading={saving} icon={<CheckCircleOutlined />}>
                    Save Configuration
                  </Button>
                  <Button
                    onClick={() => form.resetFields()}
                  >
                    Reset Changes
                  </Button>
                </Space>
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default UserLicenseConfig;
